import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useNotification } from '../hooks/useNotification';
import { useLanguageStore } from '../stores/useLanguageStore';
import authProvider from '../authProvider';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Lock, Globe, Fingerprint, Eye, EyeOff } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import ForgotPassword from './ForgotPassword';

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'vi', name: 'Tiếng Việt' },
];

interface SavedCredentials {
  username: string;
  password: string;
}

const ModernLogin = () => {
  const [loading, setLoading] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rememberPassword, setRememberPassword] = useState(false);
  const [savedCredentials, setSavedCredentials] =
    useState<SavedCredentials | null>(null);

  const { translate } = useTranslation();
  const { notify } = useNotification();
  const navigate = useNavigate();
  const { setLocale, locale } = useLanguageStore();
  const location = useLocation();

  useEffect(() => {
    // Detect iOS Safari
    const detectIOS = () => {
      const userAgent = navigator.userAgent;
      const isIOSDevice =
        /iPad|iPhone|iPod/.test(userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
      return isIOSDevice && isSafari;
    };

    setIsIOS(detectIOS());

    // Load saved credentials if they exist
    const saved = localStorage.getItem('savedCredentials');
    if (saved) {
      try {
        const credentials = JSON.parse(saved);
        setSavedCredentials(credentials);
        setRememberPassword(true);
      } catch (error) {
        console.error('Failed to parse saved credentials:', error);
        localStorage.removeItem('savedCredentials');
      }
    }
  }, []);

  // Helper function to clean form input values
  // Strips trailing whitespace, newlines, and any trailing URLs/links
  const cleanFormValue = (value: string | null | undefined): string => {
    if (!value) return '';
    
    // Convert to string and remove newlines and carriage returns
    let cleaned = value.toString().replace(/[\r\n]+/g, '');
    
    // Remove trailing URLs/links (common when pasting from messages)
    // Matches URLs at the end of the string
    cleaned = cleaned.replace(/\s*(https?:\/\/[^\s]+)\s*$/gi, '');
    
    // Trim leading and trailing whitespace
    cleaned = cleaned.trim();
    
    return cleaned;
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    // Get values from form elements
    const form = event.currentTarget;
    const formData = new FormData(form);
    const usernameOrEmail = cleanFormValue(formData.get('username')?.toString());
    const password = cleanFormValue(formData.get('password')?.toString());

    // Validate credentials
    if (!usernameOrEmail || !password) {
      setLoading(false);
      setError(translate('pos.auth.username_email_password_required'));
      return;
    }

    // Use authProvider directly
    authProvider
      .login({
        // If input looks like an email, send it as email
        ...(usernameOrEmail.includes('@')
          ? { email: usernameOrEmail }
          : { username: usernameOrEmail }),
        password,
      })
      .then(() => {
        // Don't set loading to false here - let the OAuth flow complete
        // The OAuth flow will handle navigation after tokens are obtained

        // Save credentials if remember password is checked
        if (rememberPassword) {
          const credentials = {
            username: usernameOrEmail,
            password: password,
          };
          localStorage.setItem('savedCredentials', JSON.stringify(credentials));
        } else {
          // Clear saved credentials if remember password is unchecked
          localStorage.removeItem('savedCredentials');
        }

        // Don't navigate here - let the OAuth flow handle it
      })
      .catch((error: Error) => {
        setLoading(false);
        const errorMessage =
          typeof error === 'string'
            ? error
            : typeof error === 'undefined' || !error.message
            ? translate('pos.auth.invalid_credentials')
            : error.message;

        setError(errorMessage);
        notify(errorMessage, { type: 'error' });
      });
  };

  const handlePasskeyLogin = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if WebAuthn is supported
      if (!window.PublicKeyCredential) {
        setLoading(false);
        setError(translate('pos.auth.passkey_not_supported'));
        notify(translate('pos.auth.passkey_not_supported'), { type: 'error' });
        return;
      }

      // For mobile devices, ensure we're calling from a direct user gesture
      // Don't try to focus hidden inputs on mobile as it may interfere
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      if (!isMobile) {
        // On desktop, try to focus the passkey input to trigger the browser's passkey UI
        const passkeyInput = document.getElementById('passkey');
        if (passkeyInput) {
          passkeyInput.focus();
        }
      }

      // Call login with passkey - this must be in direct response to user gesture
      await authProvider.login({ passkey: true });
      // Navigate to modern dashboard or next path
      const nextPath = (location.state as any)?.nextPathname || '/dashboard';
      navigate(nextPath);
    } catch (error: any) {
      console.error('Passkey login error:', error);
      setLoading(false);

      if (error.name === 'NoPasskeysError') {
        setError(translate('pos.auth.no_passkeys'));
        notify(translate('pos.auth.no_passkeys'), { type: 'warning' });
      } else {
        // Use the error message if available, otherwise use generic error
        const errorMessage = error.message || translate('pos.auth.passkey_error');
        setError(errorMessage);
        notify(errorMessage, { type: 'error' });
      }
    }
  };

  const handleForgotPasswordClick = () => {
    setIsResettingPassword(true);
  };

  if (isResettingPassword) {
    return <ForgotPassword />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 p-4">
      <div className="w-full max-w-md">
        <Card className="shadow-xl border-0">
          <CardHeader className="space-y-1 pb-4">
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                <Lock className="w-6 h-6 text-primary-foreground" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-center">
              {translate('pos.auth.sign_in')}
            </CardTitle>
            <CardDescription className="text-center text-muted-foreground">
              {translate('pos.auth.welcome_back')}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Hidden passkey input for WebAuthn */}
            <input
              type="text"
              name="passkey"
              id="passkey"
              autoComplete="webauthn"
              aria-label={translate('pos.auth.sign_in_with_passkey')}
              className="absolute opacity-0 pointer-events-none h-0"
              onFocus={(e) => e.target.click()}
            />

            {/* Language Selector */}
            <div className="flex items-center space-x-2">
              <Globe className="w-4 h-4 text-muted-foreground" />
              <Select value={locale} onValueChange={setLocale}>
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={translate('pos.common.action.choose_language')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map((lang) => (
                    <SelectItem key={lang.code} value={lang.code}>
                      {lang.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <form onSubmit={handleSubmit} noValidate className="space-y-3">
              {/* Error Alert */}
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* iOS Passkey Guidance */}
              {isIOS && (
                <Alert>
                  <AlertDescription>
                    {translate('pos.auth.register_passkey_ios_guidance')}
                  </AlertDescription>
                </Alert>
              )}

              {/* Username/Email Input */}
              <div className="space-y-2">
                <Label htmlFor="username">
                  {translate('pos.auth.username_or_email')}
                </Label>
                <Input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  placeholder={translate(
                    'pos.auth.username_or_email_placeholder'
                  )}
                  disabled={loading}
                  required
                  data-clarity-mask="true"
                  defaultValue={savedCredentials?.username || ''}
                />
              </div>

              {/* Password Input */}
              <div className="space-y-2">
                <Label htmlFor="password">
                  {translate('pos.auth.password')}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder={translate('pos.auth.password_placeholder')}
                    disabled={loading}
                    required
                    data-clarity-mask="true"
                    defaultValue={savedCredentials?.password || ''}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Remember Password Checkbox */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="rememberPassword"
                  checked={rememberPassword}
                  onCheckedChange={(checked) =>
                    setRememberPassword(checked as boolean)
                  }
                  disabled={loading}
                />
                <Label
                  htmlFor="rememberPassword"
                  className="text-sm font-normal cursor-pointer"
                >
                  {translate('pos.auth.remember_password')}
                </Label>
              </div>

              {/* Sign In Button */}
              <Button type="submit" className="w-full" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {translate('pos.auth.sign_in')}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <Separator className="w-full" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  {translate('pos.auth.or')}
                </span>
              </div>
            </div>

            {/* Passkey Login Button */}
            <Button
              variant="outline"
              className="w-full"
              onClick={handlePasskeyLogin}
              disabled={loading || !window.PublicKeyCredential}
            >
              <Fingerprint className="mr-2 h-4 w-4" />
              {translate('pos.auth.sign_in_with_passkey')}
            </Button>

            {/* Forgot Password Link */}
            <div className="text-center">
              <Button
                type="button"
                variant="link"
                className="text-sm text-muted-foreground hover:text-primary"
                onClick={handleForgotPasswordClick}
              >
                {translate('pos.auth.forgot_password')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ModernLogin;
